class CollectionInfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.observer = null;
    this.onLoadMoreClick = this.onLoadMoreClick.bind(this);
    this.loadNextPage = this.loadNextPage.bind(this);
  }

  connectedCallback() {
    this.grid = document.querySelector("[data-infinite-product-grid]");
    this.nextLink = this.querySelector("[data-infinite-next-link]");
    this.sentinel = this.querySelector("[data-infinite-sentinel]");
    this.loader = this.querySelector(".collection-infinite-scroll__loader");
    this.currentCount = this.querySelector("[data-infinite-current]");
    this.totalCount = this.querySelector("[data-infinite-total]");
    this.progress = this.querySelector("[data-infinite-progress]");

    if (this.nextLink) {
      this.nextLink.addEventListener("click", this.onLoadMoreClick);
    }

    this.updateState();

    if ("IntersectionObserver" in window && this.sentinel) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.loadNextPage();
          }
        },
        { rootMargin: "700px 0px 700px" }
      );
      this.observer.observe(this.sentinel);
    } else if (this.nextLink && this.dataset.nextUrl) {
      this.nextLink.hidden = false;
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.nextLink) {
      this.nextLink.removeEventListener("click", this.onLoadMoreClick);
    }
  }

  onLoadMoreClick(event) {
    event.preventDefault();
    this.loadNextPage();
  }

  async loadNextPage() {
    const nextUrl = this.dataset.nextUrl;

    if (this.isLoading || !nextUrl || !this.grid) return;

    this.setLoading(true);

    try {
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set("section_id", this.dataset.sectionId);

      const response = await fetch(url.toString());
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, "text/html");
      const nextGrid = html.querySelector("[data-infinite-product-grid]");
      const nextScroll = html.querySelector("collection-infinite-scroll");

      if (!nextGrid || !nextScroll) return;

      this.grid.insertAdjacentHTML("beforeend", nextGrid.innerHTML);
      this.dataset.nextUrl = nextScroll.dataset.nextUrl || "";
      this.dataset.currentCount = nextScroll.dataset.currentCount || this.dataset.currentCount;
      this.dataset.totalCount = nextScroll.dataset.totalCount || this.dataset.totalCount;
      this.updateState();
    } catch (error) {
      console.error("Infinite scroll failed:", error);
      if (this.nextLink && this.dataset.nextUrl) {
        this.nextLink.hidden = false;
      }
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    this.isLoading = isLoading;
    this.classList.toggle("is-loading", isLoading);

    if (this.loader) {
      this.loader.hidden = !isLoading;
    }
  }

  updateState() {
    const current = parseInt(this.dataset.currentCount, 10) || 0;
    const total = parseInt(this.dataset.totalCount, 10) || current;
    const hasNextPage = Boolean(this.dataset.nextUrl);
    const progressPercent = total > 0 ? Math.min((current / total) * 100, 100) : 100;

    if (this.currentCount) this.currentCount.textContent = current;
    if (this.totalCount) this.totalCount.textContent = total;
    if (this.progress) this.progress.style.width = `${progressPercent}%`;

    if (this.nextLink) {
      this.nextLink.href = this.dataset.nextUrl || "#";
      this.nextLink.hidden = "IntersectionObserver" in window || !hasNextPage;
    }

    this.classList.toggle("is-complete", !hasNextPage);
  }
}

if (!customElements.get("collection-infinite-scroll")) {
  customElements.define("collection-infinite-scroll", CollectionInfiniteScroll);
}
